import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiLogin, apiMe, apiLogout, clearToken, getToken, setToken, AuthUser } from '../services/server-api';

interface AuthContextValue {
  user: AuthUser | null;
  llmReady: boolean;
  checking: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  updateQuota: (usedCount: number) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [llmReady, setLlmReady] = useState(false);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setChecking(false);
      return;
    }
    try {
      const me = await apiMe();
      setUser(me.user);
      setLlmReady(me.llmReady);
    } catch {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    const result = await apiLogin(username, password);
    setToken(result.token);
    setUser(result.user);
    setLlmReady(result.llmReady);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // 忽略登出接口异常，本地一定清除
    }
    clearToken();
    setUser(null);
  }, []);

  const updateQuota = useCallback((usedCount: number) => {
    setUser(prev => (prev ? { ...prev, usedCount, remainingQuota: Math.max(0, prev.questionLimit - usedCount) } : prev));
  }, []);

  return (
    <AuthContext.Provider value={{ user, llmReady, checking, login, logout, refresh, updateQuota }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth 必须在 AuthProvider 内使用');
  }
  return ctx;
};
